import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface ContextValue {
    setCollapseLocalStorageKey: (storageKey: string) => void;
    setIsCollapsed: (isCollapsed: boolean) => void;
    isCollapsed: boolean;
    setBreadcrumbs: (breadcrumbs: React.ReactNode[]) => void;
    breadcrumbs: React.ReactNode[];
}

const NavBarContext = React.createContext<ContextValue | null>(null);

interface ProviderProps {
    children: ReactNode;
}

export const NavBarProvider = ({ children }: ProviderProps) => {
    const [collapseStorageKey, setCollapseLocalStorageKey] = useState('');
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState<React.ReactNode[]>([]);

    const loadCollapsePreference = useCallback((collapseStoragekey: string) => {
        setIsCollapsed(localStorage.getItem(collapseStoragekey) === 'true');
        setCollapseLocalStorageKey(collapseStoragekey);
    }, []);

    const saveCollapsePreference = useCallback(
        (isCollapsed: boolean) => {
            if (collapseStorageKey) {
                const value = isCollapsed ? 'true' : 'false';
                localStorage.setItem(collapseStorageKey, value);
            }
            setIsCollapsed(isCollapsed);
        },
        [collapseStorageKey],
    );

    // useMemo required - context value passed to provider.
    // React Compiler doesn't optimize context values automatically.
    const value = useMemo(
        () => ({
            setCollapseLocalStorageKey: loadCollapsePreference,
            isCollapsed,
            setIsCollapsed: saveCollapsePreference,
            setBreadcrumbs,
            breadcrumbs,
        }),
        [loadCollapsePreference, isCollapsed, saveCollapsePreference, breadcrumbs],
    );

    return <NavBarContext.Provider value={value}>{children}</NavBarContext.Provider>;
};

export function useNavBar() {
    const ctx = useContext(NavBarContext);
    if (!ctx) {
        throw new Error('Nav bar provider is not detected');
    }
    return ctx;
}
